<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <div v-if="dataExists">
    <checklist-step v-if="form.publishedAt == null" stage="complete">
      <template #title>{{ $t('steps[0].title') }}</template>
      <p>
        <strong>{{ $t('steps[0].body[0]') }}</strong>
        {{ $t('steps[0].body[1]') }}
      </p>
    </checklist-step>
    <checklist-step stage="current">
      <template #title>{{ $t('steps[1].title') }}</template>
      <p v-if="status">
        {{ $t('steps[1].body[0].status') }}
      </p>
      <i18n v-else tag="p" path="steps[1].body[0].link.full">
        <template #upload>
          <router-link :to="formPath('draft')">{{ $t('steps[1].body[0].link.upload') }}</router-link>
        </template>
      </i18n>
    </checklist-step>
    <checklist-step v-if="attachments.length !== 0"
      :stage="missingAttachmentCount === 0 ? 'complete' : 'current'">
      <template #title>{{ $t('steps[2].title') }}</template>
      <p>
        <i18n :tag="false" path="steps[2].body[0].full">
          <template #mediaFiles>
            <router-link :to="formPath('draft/attachments')">{{ $t('steps[2].body[0].mediaFiles') }}</router-link>
          </template>
        </i18n>
        &nbsp;
        <doc-link to="central-forms/#forms-with-attachments">{{ $t('clickForInfo') }}</doc-link>
      </p>
    </checklist-step>
    <checklist-step
      :stage="formDraft.submissions !== 0 ? 'complete' : 'current'">
      <template #title>{{ $t('steps[3].title') }}</template>
      <p>
        <i18n :tag="false" path="steps[3].body[0].full">
          <template #test>
            <router-link :to="formPath('draft/testing')">{{ $t('steps[3].body[0].test') }}</router-link>
          </template>
        </i18n>
        &nbsp;
        <doc-link to="central-forms/#working-with-form-drafts">{{ $t('clickForInfo') }}</doc-link>
      </p>
    </checklist-step>
    <checklist-step stage="current">
      <template #title>{{ $t('steps[4].title') }}</template>
      <p>
        <template v-if="status">
          {{ $t('steps[4].body[0].status') }}
        </template>
        <i18n v-else :tag="false" path="steps[4].body[0].link.full">
          <template #publish>
            <router-link :to="formPath('draft')">{{ $t('steps[4].body[0].link.publish') }}</router-link>
          </template>
        </i18n>
        &nbsp;
        <doc-link to="central-forms/#working-with-form-drafts">{{ $t('clickForInfo') }}</doc-link>
      </p>
    </checklist-step>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ChecklistStep from '../checklist-step.vue';
import DocLink from '../doc-link.vue';
import routes from '../../mixins/routes';
import { requestData } from '../../store/modules/request';

export default {
  name: 'FormDraftChecklist',
  components: { ChecklistStep, DocLink },
  mixins: [routes()],
  props: {
    // Indicates whether the current route path is .../draft.
    status: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // The component does not assume that this data will exist when the
    // component is created.
    ...requestData([
      'form',
      { key: 'formDraft', getOption: true },
      { key: 'attachments', getOption: true }
    ]),
    ...mapGetters(['missingAttachmentCount']),
    dataExists() {
      return this.$store.getters.dataExists([
        'form',
        'formDraft',
        'attachments'
      ]);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    "clickForInfo": "Click here to find out more.",
    "steps": [
      {
        // This is the title of a checklist item.
        "title": "Upload initial Form definition",
        "body": [
          "Great work!",
          "Your Form design has been loaded successfully."
        ]
      },
      {
        // This is the title of a checklist item.
        "title": "Upload revised Form definition (optional)",
        "body": [
          {
            // This refers to changes to the form definition as opposed to just the media associated with the Form.
            "status": "If you have made changes to the Form itself, including question text or logic rules, now is the time to upload the new XML or XLSForm using the button to the right.",
            "link": {
              // This refers to changes to the form definition as opposed to just the media associated with the Form.
              "full": "If you have made changes to the Form itself, including question text or logic rules, now is the time to {upload} the new XML or XLSForm.",
              "upload": "upload"
            }
          }
        ]
      },
      {
        // This is the title of a checklist item.
        "title": "Upload Form Media Files",
        "body": [
          {
            "full": "Your Form design references files that are needed in order to present your Form. You can upload new or updated copies of these for distribution under the {mediaFiles} tab.",
            "mediaFiles": "Media Files"
          }
        ]
      },
      {
        // This is the title of a checklist item.
        "title": "Test the Form on your mobile device",
        "body": [
          {
            "full": "You can {test} a Form to be sure it works the way you expect. Test Submissions are not included in your final data.",
            "test": "test"
          }
        ]
      },
      {
        // This is the title of a checklist item.
        "title": "Publish the Draft",
        "body": [
          {
            "status": "When you are sure your Draft is ready and you wish to roll it out to your devices in the field, you can publish it using the button to the right.",
            "link": {
              "full": "When you are sure your Draft is ready and you wish to roll it out to your devices in the field, you can {publish} it.",
              "publish": "publish"
            }
          }
        ]
      }
    ]
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "clickForInfo": "Klikněte sem a dozvíte se více",
    "steps": [
      {
        "title": "Nahrajte počáteční definici formuláře",
        "body": [
          "Skvělá práce!",
          "Návrh formuláře byl úspěšně načten."
        ]
      },
      {
        "title": "Nahrát revidovanou definici formuláře (volitelné)",
        "body": [
          {
            "status": "Pokud jste provedli změny v samotném formuláři, včetně textu otázek nebo logických pravidel, je nyní čas nahrát nový XML nebo XLSForm pomocí tlačítka napravo.",
            "link": {
              "full": "Pokud jste provedli změny v samotném formuláři, včetně textu otázek nebo logických pravidel, je nyní čas na {upload} nového XML nebo XLSForm.",
              "upload": "nahrát"
            }
          }
        ]
      },
      {
        "title": "Nahrát mediální soubory formuláře",
        "body": [
          {
            "full": "Návrh formuláře odkazuje na soubory, které jsou potřebné k prezentaci formuláře. Na kartě {mediaFiles} můžete nahrát nové, nebo aktualizované kopie těchto souborů určených k distribuci.",
            "mediaFiles": "Mediální soubory"
          }
        ]
      },
      {
        "title": "Vyzkoušejte formulář na svém mobilním zařízení",
        "body": [
          {
            "full": "Můžete {test} formulář abyste se ujistili, že funguje tak, jak očekáváte. Testovací odeslání nejsou součástí vašich konečných údajů.",
            "test": "otestovat"
          }
        ]
      },
      {
        "title": "Publikovat koncept",
        "body": [
          {
            "status": "Pokud jste si jisti, že je koncept připraven a chcete jej uvést do provozu v terénu, můžete jej publikovat pomocí tlačítka napravo.",
            "link": {
              "full": "Pokud jste si jisti, že je koncept připraven a chcete jej uvést do provozu v terénu, můžete ho {publish}.",
              "publish": "publikovat"
            }
          }
        ]
      }
    ]
  },
  "de": {
    "clickForInfo": "Klicken Sie hier, um mehr zu erfahren.",
    "steps": [
      {
        "title": "Formulardefinition hochladen",
        "body": [
          "Gute Arbeit!",
          "Ihr Formular wurde erfolgreich hochgeladen."
        ]
      },
      {
        "title": "Geändert Formulardefinition hochladen (optional)",
        "body": [
          {
            "status": "Wenn Sie Änderungen am Formular selbst vorgenommen haben, etwa am Fragetext oder an den Logikregeln, sollten Sie jetzt das neue XML- oder XLSFormular mit der Schaltfläche auf der rechten Seite hochladen.",
            "link": {
              "full": "Wenn Sie Änderungen am Formular selbst vorgenommen haben, etwa am Fragetext oder an den Logikregeln, sollten Sie jetzt das neue Formular {upload}.",
              "upload": "hochladen"
            }
          }
        ]
      },
      {
        "title": "Mediendateien hochladen",
        "body": [
          {
            "full": "Ihre Referenzdateien für den Formularentwurf werden für die Darstellung des Formulars benötigt. Sie können neue oder geänderte Formulare über den Reiter {mediaFiles} hochladen.",
            "mediaFiles": "Mediendateien"
          }
        ]
      },
      {
        "title": "Testen Sie das Formular auf Ihrem Mobilgerät",
        "body": [
          {
            "full": "Sie können ein Formular {test}, um sicher zu sein, dass es wie erwartet funktioniert. Test-Übermittlungen sind in den endgültigen Daten nicht enthalten.",
            "test": "testen"
          }
        ]
      },
      {
        "title": "Den Entwurf veröffentlichen",
        "body": [
          {
            "status": "Wenn Sie sicher sind, dass Ihr Entwurf fertig ist und Sie ihn für den Feldeinsatz bereitstellen wollen, können Sie ihn mit der Schaltfläche rechts veröffentlichen.",
            "link": {
              "full": "Wenn Sie sicher sind, dass Ihr Entwurf fertig ist und Sie ihn für den Feldeinsatz bereitstellen wollen, können sie ihn {publish}.",
              "publish": "veröffentlichen"
            }
          }
        ]
      }
    ]
  },
  "es": {
    "clickForInfo": "Haga clic aquí para descubrir más.",
    "steps": [
      {
        "title": "Subir definición de formulario inicial",
        "body": [
          "Buen trabajo.",
          "Su diseño de formulario se ha cargado correctamente."
        ]
      },
      {
        "title": "Subir definición de formulario revisada (opcional)",
        "body": [
          {
            "status": "Si ha realizado cambios en el formulario en sí, incluido el texto de la pregunta o las reglas lógicas, ahora es el momento de cargar el nuevo XML o XLSForm utilizando el botón a la derecha.",
            "link": {
              "full": "Si ha realizado cambios en el formulario en sí, incluido el texto de la pregunta o las reglas lógicas, ahora es el momento de {upload} el nuevo XML o XLSForm.",
              "upload": "subir"
            }
          }
        ]
      },
      {
        "title": "Subir archivos multimedia de formulario",
        "body": [
          {
            "full": "El diseño de su formulario hace referencia a los archivos necesarios para presentar su formulario. Puede cargar copias nuevas o actualizadas para su distribución en la pestaña {mediaFiles}.",
            "mediaFiles": "archivos multimedia"
          }
        ]
      },
      {
        "title": "Pruebe el formulario en su dispositivo móvil",
        "body": [
          {
            "full": "Puede {test} un formulario para asegurarse de que funciona como usted espera. Los envíos de prueba no son incluidos en sus datos finales.",
            "test": "probar"
          }
        ]
      },
      {
        "title": "Publicar el borrador",
        "body": [
          {
            "status": "Cuando esté seguro de que su borrador está listo y desea extenderlo a sus dispositivos en el campo, puede publicarlo usando el botón a la derecha.",
            "link": {
              "full": "Cuando esté seguro de que su borrador está listo y desea extenderlo a sus dispositivos en el campo, puede {publish}.",
              "publish": "publicarlo"
            }
          }
        ]
      }
    ]
  },
  "fr": {
    "clickForInfo": "Cliquez ici pour en savoir plus.",
    "steps": [
      {
        "title": "Téléverser la définition initiale du formulaire",
        "body": [
          "Beau travail !",
          "Votre design de formulaire a été téléversé avec succès."
        ]
      },
      {
        "title": "Téléverser une révision de la définition du formulaire (optionnel)",
        "body": [
          {
            "status": "Si vous avez apporté des changements au formulaire lui-même, y compris au texte des questions ou au règles logiques, il est temps de téléverser le nouveau XML ou XLSForm en utilisant le bouton de droite.",
            "link": {
              "full": "Si vous avez apporté des changements au formulaire lui-même, y compris au texte des questions ou au règles logiques, il est temps de {upload} the new XML or XLSForm.",
              "upload": "téléverser"
            }
          }
        ]
      },
      {
        "title": "Téléverser les fichiers média du formulaire",
        "body": [
          {
            "full": "Le modèle de votre formulaire fait référence à des fichiers qui sont nécessaires pour présenter votre formulaire. Vous pouvez télécharger des copies nouvelles ou mises à jour de ces fichiers pour les distribuer sous l'onglet {mediaFiles}.",
            "mediaFiles": "Fichiers média"
          }
        ]
      },
      {
        "title": "Testez le formulaire sur votre appareil mobile",
        "body": [
          {
            "full": "Vous pouvez {test} un formulaire pour vérifier qu'il fonctionne comme prévu. Les soumissions de test ne seront pas incluses dans vos vraies données.",
            "test": "tester"
          }
        ]
      },
      {
        "title": "Publier l'ébauche",
        "body": [
          {
            "status": "Quand vous êtes sûr que votre ébauche est prête et que vous souhaitez la déployer sur vos appareils sur le terrain, vous pouvez la publier en utilisant le bouton de droite.",
            "link": {
              "full": "Quand vous êtes sûr que votre ébauche est prête et que vous souhaitez la déployer sur vos appareils sur le terrain, vous pouvez la {publish}.",
              "publish": "publier"
            }
          }
        ]
      }
    ]
  }
}
</i18n>
